/**
 * Shared TypeScript types for the Spirit of Shabbat loading animation system
 */

// Animation phases for the loader state machine
export type AnimationPhase = 'intro' | 'loop' | 'exit' | 'complete';

// Core animation module interface - all canvas elements implement this
export interface AnimationModule {
  /** Unique identifier for the module */
  id: string;

  /** Initialize the module with canvas context and dimensions */
  init(ctx: CanvasRenderingContext2D, width: number, height: number): void;

  /** Update animation state based on time delta */
  update(deltaTime: number, phase: AnimationPhase, progress: number): void;

  /** Render the module to canvas */
  render(ctx: CanvasRenderingContext2D): void;

  /** Handle resize events */
  resize(width: number, height: number): void;

  /** Cleanup resources */
  destroy(): void;

  /** Optional: trigger exit animation */
  triggerExit?(): void;
}

// Configuration for the animation engine
export interface AnimationEngineConfig {
  /** Target frames per second (default: 60) */
  targetFPS?: number;

  /** Minimum time to show intro animation in ms (default: 1500) */
  introMinDuration?: number;

  /** Minimum pause time in loop phase before exit in ms (default: 1000) */
  loopPauseDuration?: number;

  /** Duration of exit animation in ms (default: 1000) */
  exitDuration?: number;

  /** Whether to respect prefers-reduced-motion (default: true) */
  respectReducedMotion?: boolean;
}

// Color palette for consistent theming
export const LOADER_COLORS = {
  // Primary purple theme
  primary: '#8e4585',
  primaryDark: '#753a6f',
  primaryDeep: '#63345e',
  primaryLight: '#c075c0',
  primaryPale: '#e5bce5',
  primaryFaint: '#f0d2f0',
  primaryGhost: '#f8e8f8',
  primaryWhisper: '#fcf4fc',

  // Warm gold/flame colors
  gold: '#d4a574',
  goldBright: '#f0c78a',
  goldPale: '#fef3e2',
  amber: '#e8a855',
  orange: '#e07830',

  // Candle/flame colors
  flameOuter: '#ff9500',
  flameMiddle: '#ffcc00',
  flameInner: '#fff5d4',
  flameGlow: 'rgba(255, 200, 100, 0.4)',

  // Parchment/scroll colors
  parchment: '#fef3e2',
  parchmentDark: '#f5e6d0',
  // Purple-tinted scroll handles to match theme
  scrollHandle: '#8e4585',
  scrollHandleLight: '#a85a9f',
  scrollHandleDark: '#6d3566',

  // Background gradient stops
  bgLight: '#fcf4fc',
  bgMid: '#f8e8f8',
  bgDark: '#f0d2f0',
} as const;

// Typography settings for Hebrew text
export const HEBREW_FONTS = {
  primary: '"Times New Roman", "David", "Narkisim", serif',
  fallback: 'serif',
} as const;

// Easing function type
export type EasingFunction = (t: number) => number;

// Particle interface for the particle system
export interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  color: string;
  alpha: number;
}

// Torah scroll configuration
export interface TorahScrollConfig {
  centerX: number;
  centerY: number;
  width: number;
  height: number;
  unrollProgress: number;
}

// Candle configuration
export interface CandleConfig {
  x: number;
  y: number;
  width: number;
  height: number;
  flamePhase: number;
  glowIntensity: number;
}

// Loading state for coordination with React
export interface LoadingState {
  phase: AnimationPhase;
  progress: number;
  isReady: boolean;
  hasError: boolean;
  errorMessage?: string;
}

// Callback types
export type OnPhaseChange = (phase: AnimationPhase) => void;
export type OnComplete = () => void;
